/**
 * Authentication context: owns the current-user session for the whole SPA.
 *
 * - Bootstraps the session on mount (restores from cookie/token via `/auth/me`).
 * - Exposes `login` / `register` / `logout` and role helpers used for gating.
 * - Wires the API client's "session expired" hook so a failed token refresh
 *   cleanly signs the user out and clears cached data.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authApi, LoginInput, RegisterInput } from "../api/auth";
import { setSessionExpiredHandler } from "../lib/apiClient";
import { tokenStore } from "../lib/tokenStore";
import { Role, User } from "../types/models";

const MANAGEMENT_ROLES: Role[] = [Role.ADMIN, Role.MANAGER];

interface AuthContextValue {
  user: User | null;
  /** True only during the initial session bootstrap. */
  isBootstrapping: boolean;
  isAuthenticated: boolean;
  isManagement: boolean;
  isClient: boolean;
  hasRole: (...roles: Role[]) => boolean;
  login: (input: LoginInput) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const bootstrapped = useRef(false);

  const handleSignedOut = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  // A failed silent refresh (interceptor) ends the session.
  useEffect(() => {
    setSessionExpiredHandler(handleSignedOut);
  }, [handleSignedOut]);

  // Restore the session once on first mount.
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    // No persisted token *and* no chance of a cookie session → skip the round-trip.
    if (!tokenStore.get()) {
      setIsBootstrapping(false);
      return;
    }
    authApi
      .me()
      .then(setUser)
      .catch(() => {
        tokenStore.clear();
        setUser(null);
      })
      .finally(() => setIsBootstrapping(false));
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const session = await authApi.login(input);
    tokenStore.set(session.accessToken);
    setUser(session.user);
    return session.user;
  }, []);

  const register = useCallback(async (input: RegisterInput) => {
    const session = await authApi.register(input);
    tokenStore.set(session.accessToken);
    setUser(session.user);
    return session.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      handleSignedOut();
    }
  }, [handleSignedOut]);

  const refreshUser = useCallback(async () => {
    const fresh = await authApi.me();
    setUser(fresh);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const hasRole = (...roles: Role[]) =>
      user ? roles.includes(user.role) : false;
    return {
      user,
      isBootstrapping,
      isAuthenticated: Boolean(user),
      isManagement: user ? MANAGEMENT_ROLES.includes(user.role) : false,
      isClient: user?.role === Role.CLIENT,
      hasRole,
      login,
      register,
      logout,
      refreshUser,
    };
  }, [user, isBootstrapping, login, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an <AuthProvider>");
  return ctx;
}
